import React, { FC } from "react";
// components
import NextImage from "next/image";
import Button from "src/components/ui/button";
import Select from "src/components/ui/Select";
import SelectDatepicker from "src/components/ui/SelectDatepicker";
// const
import { EVariants } from "src/constants/variants";
// redux
import {
  setRequestDate,
  setRequestTime,
  setRequestStep,
  setRequestClinicDetails,
  setRequestRating,
} from "src/redux/slices/appointment";
// hooks
import { useAppDispatch } from "src/hooks";
import { useRouter } from "next/router";
import { useReviews } from "src/hooks/services";
// types
import { TClinicDetails } from "src/types";
// theme
import { amplitudeEvent, formatEventName } from "src/libs/amplitude";
import { Action, Body, Caption, Headline } from "src/theme/typography";
import { FlexColumn, FlexRow, Line, SmallCard } from "src/components/ui/common";
import { colors } from "src/theme";
import Link from "src/components/ui/Link";
import { ContactLink } from "./styles";

type Time = {
  label: string;
  value: string;
};

type TContactData = {
  id: string;
  label: string;
  href: string;
  icon: string;
};

const TIME_OPTIONS = [
  { label: "Morning", value: "morning" },
  { label: "Afternoon", value: "afternoon" },
];

interface IUnclaimedAppointmentRequestWidget {
  clinic: TClinicDetails;
  claimClinic: () => void;
}

const UnclaimedAppointmentRequestWidget: FC<IUnclaimedAppointmentRequestWidget> = ({
  clinic,
  claimClinic,
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const RatingService = useReviews();

  const selectDate = (data: string) => {
    dispatch(setRequestDate({ date: data }));
  };

  const selectTime = (data: Time | null) => {
    if (data) dispatch(setRequestTime({ time: data.label }));
  };

  const startRequest = async () => {
    const reviewsStatistic = await RatingService.loadReviewsStatistic(`clinic=${clinic.id}`);
    amplitudeEvent(formatEventName("clinic_details", "side_panel", "request_appointment"));

    dispatch(
      setRequestClinicDetails({
        clinic: {
          id: clinic.id,
          name: clinic.name,
          address: clinic.address,
          distance: NaN, // hide distance text
          original_logo_url: clinic.original_logo_url,
          logo_url: clinic.logo_url,
          data: {
            lat: clinic.geo_data.data.lat,
            lng: clinic.geo_data.data.lng,
          },
          slug: clinic.slug,
        },
      }),
    );
    dispatch(setRequestRating({ rating: reviewsStatistic.rating }));
    dispatch(setRequestStep({ step: 4 }));
    router.push({
      pathname: "/appointment/request",
      query: { clinic: clinic?.slug, id: clinic?.id },
    });
  };

  const WebsiteContact = clinic.website
    ? [
        {
          id: "website",
          href: clinic.website,
          label: clinic.website.replace(/(http(s?):\/\/|www.)/g, "").split("/")[0],
          icon: "/icons/contact/language.svg",
        } as unknown as TContactData,
      ]
    : [];

  const PhoneContact = clinic.phone
    ? [
        {
          id: "phone",
          href: `tel:${clinic.phone}`,
          label: clinic.phone,
          icon: "/icons/contact/call.svg",
        } as unknown as TContactData,
      ]
    : [];

  const FacebookContact = clinic.social?.facebook
    ? [
        {
          id: "facebook",
          href: clinic.social?.facebook,
          label: "Facebook",
          icon: "/icons/contact/facebook.svg",
        } as unknown as TContactData,
      ]
    : [];

  const contactData: Array<TContactData> = [...WebsiteContact, ...FacebookContact, ...PhoneContact];

  return (
    <SmallCard gap="24px" as="aside">
      {clinic.request_an_appointment === true && (
        <>
          <Headline as="h2">Request an appointment</Headline>
          <FlexColumn gap="12px">
            <SelectDatepicker onChangeCb={selectDate} />
            <FlexRow gap="12px">
              <Body style={{ minWidth: "fit-content" }}>in the</Body>
              <Select
                options={TIME_OPTIONS}
                defaultValue={TIME_OPTIONS[0]}
                isClearable={false}
                onChange={selectTime}
              />
            </FlexRow>
          </FlexColumn>

          <Button variant={EVariants.PRIMARY} style={{ width: "100%" }} onClick={startRequest}>
            Request appointment
          </Button>

          <Line />
        </>
      )}
      <FlexColumn gap="12px">
        <Headline as="h2">Contact</Headline>
        <FlexColumn>
          {contactData.map(({ id, label, href, icon }) => (
            <a key={id} href={href} target="_blank" rel="noreferrer">
              <ContactLink
                gap="12px"
                onClick={() => amplitudeEvent(formatEventName("clinic_details", "side_panel", id))}
              >
                <NextImage src={icon} width={24} height={24} alt="" />
                <Caption textColor={colors.greyDark}>{label}</Caption>
              </ContactLink>
            </a>
          ))}
        </FlexColumn>
      </FlexColumn>
      <Line />
      {clinic.from_petsapp !== true && (
        <FlexRow gap="6px">
          <Caption as="h2">Is this your business?</Caption>
          <Action as="button" textColor={colors.main} onClick={claimClinic}>
            Claim this business
          </Action>
        </FlexRow>
      )}
    </SmallCard>
  );
};

export default UnclaimedAppointmentRequestWidget;
